/**
 * Faction ops I (website roadmap S19): your faction's armory history, money moves and balances (the bank ledger), chain
 * reports with their bonus hits, and raids & territory — read with the keys members gave the Discord bot for their own
 * faction (never anyone else's faction), shown to that faction's members only.
 *
 * Terms (packages/shared key-terms.ts): reading your faction with your bot key and handing it to your faction mates for
 * up to an hour, in memory, is in the table since version 5 ("what your key reads of your faction"). Keeping it LONGER
 * — the faction's history on the server, FACTION_OPS_KEEP_DAYS — is new: only with a key whose owner agreed to
 * FACTION_HISTORY_TERMS. Until then each read is kept an hour in memory and gone.
 *
 * Money is never moved by Omni-Torn: a banker gets a link to Torn's own "Give to user" form, filled in by the extension
 * (bankGiveLink / readGiveLink), and presses Torn's button themself — one real click per money move, on Torn.
 *
 * The Torn shapes (v2): /faction/news?cat=… { news: [{ id, text, timestamp }] } (text with links when striptags=false),
 * /faction/chains { chains: [{ id, chain, respect, start, end }] }, /faction/{chainId}/chainreport { chainreport: { id,
 * faction_id, start, end, details, bonuses, attackers, non_attackers } }, /faction/raids { raids: [{ id, start, end,
 * aggressor, defender }] }, /faction/territorywars { territorywars: [{ id|war_id, territory, start, end, target, result?,
 * winner?, factions }] }, /faction/territory { territory: [{ id, sector, size, density, slots, respect, acquired_at,
 * racket }] }, /faction/balance { balance: { faction: { money, points }, members: [{ id, username, money, points }] } }.
 * Read leniently: Torn doesn't document the news lines' wording.
 */

/** The terms version that keeps a faction's history on the server (beyond the hour in memory). */
export const FACTION_HISTORY_TERMS = 9;
/** How long a faction's history is kept (from FACTION_HISTORY_TERMS). */
export const FACTION_OPS_KEEP_DAYS = 400;
/** Without the terms: each read kept this long, in memory. */
export const FACTION_OPS_MEMORY_MS = 60 * 60_000;

/** The parts of the faction's history. */
export type FactionOpsPart = 'armory' | 'funds' | 'chains' | 'raids';
export const FACTION_OPS_PARTS: readonly FactionOpsPart[] = ['armory', 'funds', 'chains', 'raids'];

/** Torn's news categories each part reads. */
export const FACTION_OPS_NEWS: Record<'armory' | 'funds', readonly string[]> = {
  armory: ['armoryAction', 'armoryDeposit'],
  funds: ['depositFunds', 'giveFunds'],
};

export interface OpsPerson {
  /** Torn player id; 0 when the line named them without a link. */
  id: number;
  name: string;
}

export type ArmoryAction = 'used' | 'filled' | 'loaned' | 'returned' | 'retrieved' | 'gave' | 'deposited' | 'other';
export const ARMORY_ACTIONS: readonly ArmoryAction[] = ['used', 'filled', 'loaned', 'returned', 'retrieved', 'gave', 'deposited', 'other'];

/** One line of the armory's news. */
export interface ArmoryLine {
  /** Torn's news id. */
  id: string;
  /** Seconds. */
  at: number;
  action: ArmoryAction;
  /** Who did it. */
  who: OpsPerson | null;
  /** The other member (loaned to, retrieved from, gave to). */
  to: OpsPerson | null;
  /** The item (or "points"). */
  item: string | null;
  count: number;
  /** Faction points used (refills) rather than an item. */
  points: boolean;
  /** The line as Torn wrote it, without its markup. */
  text: string;
}

export type FundsAction = 'deposit' | 'give' | 'adjust' | 'other';

/** One money or points move in the faction's news. */
export interface FundsLine {
  id: string;
  at: number;
  action: FundsAction;
  /** The member whose balance it is (who deposited, who was given it). */
  who: OpsPerson | null;
  /** Who did it for them (the banker who gave, the officer who changed a balance). */
  by: OpsPerson | null;
  /** Money in dollars or a number of points; an adjustment can be below 0. */
  amount: number;
  kind: 'cash' | 'points';
  text: string;
}

const obj = (x: unknown): Record<string, unknown> | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Record<string, unknown>) : null);
const int = (x: unknown): number => {
  const v = typeof x === 'string' ? Number(x.replace(/,/g, '')) : Number(x);
  return Number.isFinite(v) ? Math.round(v) : 0;
};
const numOr = (x: unknown, d: number | null = null): number | null => {
  const v = typeof x === 'string' && x.trim() ? Number(x) : typeof x === 'number' ? x : NaN;
  return Number.isFinite(v) ? v : d;
};
const list = (x: unknown): unknown[] => (Array.isArray(x) ? x : obj(x) ? Object.values(x as Record<string, unknown>) : []);

const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', '#039': "'", '#39': "'" };
const decode = (s: string) => s.replace(/&(#?\w+);/g, (m, e: string) => ENTITIES[e.toLowerCase()] ?? (/^#\d+$/.test(e) ? String.fromCharCode(Number(e.slice(1))) : m));

/**
 * A news line's text → its plain text and the players it links (in order), with each link replaced by "@0", "@1"… so
 * the wording can be matched. Lines without links (striptags) keep the names in the text (no ids).
 */
export function tokenizeNews(html: string): { plain: string; tokens: string; people: OpsPerson[] } {
  const people: OpsPerson[] = [];
  const raw = String(html ?? '');
  const withTokens = raw.replace(/<a\b[^>]*?XID=(\d+)[^>]*>([\s\S]*?)<\/a>/gi, (_m, id: string, name: string) => {
    people.push({ id: Number(id), name: decode(name.replace(/<[^>]+>/g, '')).trim() });
    return `@${people.length - 1}`;
  });
  const clean = (s: string) => decode(s.replace(/<[^>]+>/g, '')).replace(/\s+/g, ' ').trim();
  const tokens = clean(withTokens);
  const plain = tokens.replace(/@(\d+)/g, (_m, i: string) => people[Number(i)]?.name ?? '');
  return { plain: plain.slice(0, 300), tokens, people };
}

/** "@0" → that person; a name at the start of a line without links → a person with id 0. */
const personAt = (people: OpsPerson[], token: string | undefined): OpsPerson | null => {
  if (!token) return null;
  const m = /^@(\d+)$/.exec(token.trim());
  if (m) return people[Number(m[1])] ?? null;
  const name = token.trim();
  return name ? { id: 0, name: name.slice(0, 40) } : null;
};

/** Torn's news answer → its lines { id, at, text } (newest first). */
export function readNews(body: unknown): Array<{ id: string; at: number; text: string }> {
  const b = obj(body);
  const out: Array<{ id: string; at: number; text: string }> = [];
  for (const [k, raw] of Array.isArray(b?.news) ? (b!.news as unknown[]).map((x, i) => [String(i), x] as const) : Object.entries(obj(b?.news) ?? {})) {
    const n = obj(raw);
    if (!n) continue;
    const at = int(n.timestamp);
    const text = String(n.text ?? n.news ?? '');
    const id = String(n.id ?? k).slice(0, 40);
    if (!at || !text) continue;
    out.push({ id, at, text });
  }
  return out.sort((a, b) => b.at - a.at);
}

const WHO = '(@\\d+|[^@]+?)';

/** One armory news line → what happened (null when it isn't a line at all). */
export function parseArmoryLine(n: { id: string; at: number; text: string }): ArmoryLine {
  const { plain, tokens, people } = tokenizeNews(n.text);
  const base = { id: n.id, at: n.at, text: plain };
  let m: RegExpExecArray | null;
  // "X used 25 of the faction's points to refill their energy"
  if ((m = new RegExp(`^${WHO} used ([\\d,]+) of the faction'?s points\\b`, 'i').exec(tokens))) return { ...base, action: 'used', who: personAt(people, m[1]), to: null, item: 'points', count: int(m[2]), points: true };
  // "X used one of the faction's Xanax items" · "X filled one of the faction's Empty Blood Bag items"
  if ((m = new RegExp(`^${WHO} (used|filled) (one|[\\d,]+) of the faction'?s (.+?) items?\\b`, 'i').exec(tokens)))
    return { ...base, action: m[2]!.toLowerCase() as ArmoryAction, who: personAt(people, m[1]), to: null, item: m[4]!.trim(), count: m[3]!.toLowerCase() === 'one' ? 1 : int(m[3]), points: false };
  // "X loaned 1x Flak Jacket to Y from the faction armory" · "X gave 2x Xanax to Y" · "X retrieved 1x Flak Jacket from Y"
  if ((m = new RegExp(`^${WHO} (loaned|gave|retrieved) ([\\d,]+) ?x ?(.+?) (to|from) ${WHO}(?: from the faction armou?ry| to the faction armou?ry)?\\.?$`, 'i').exec(tokens)))
    return { ...base, action: m[2]!.toLowerCase() as ArmoryAction, who: personAt(people, m[1]), to: personAt(people, m[6]), item: m[4]!.trim(), count: int(m[3]), points: false };
  // "X returned 1x Flak Jacket (to the faction armory)"
  if ((m = new RegExp(`^${WHO} returned ([\\d,]+) ?x ?(.+?)(?: to the faction(?:'s)? armou?ry)?\\.?$`, 'i').exec(tokens)))
    return { ...base, action: 'returned', who: personAt(people, m[1]), to: null, item: m[3]!.trim(), count: int(m[2]), points: false };
  // "X deposited 5 x Xanax" · "X donated 5x Xanax to the faction"
  if ((m = new RegExp(`^${WHO} (?:deposited|donated) ([\\d,]+) ?x ?(.+?)(?: (?:in)?to the faction(?:'s)?(?: armou?ry)?)?\\.?$`, 'i').exec(tokens)))
    return { ...base, action: 'deposited', who: personAt(people, m[1]), to: null, item: m[3]!.trim(), count: int(m[2]), points: false };
  return { ...base, action: 'other', who: people[0] ?? null, to: people[1] ?? null, item: null, count: 0, points: false };
}

const AMOUNT = '(\\$[\\d,]+|[\\d,]+ points?)';
const amountOf = (s: string): { amount: number; kind: 'cash' | 'points' } => (s.startsWith('$') ? { amount: int(s.slice(1)), kind: 'cash' } : { amount: int(s.replace(/ points?$/, '')), kind: 'points' });

/** One funds news line → the money or points move. */
export function parseFundsLine(n: { id: string; at: number; text: string }): FundsLine {
  const { plain, tokens, people } = tokenizeNews(n.text);
  const base = { id: n.id, at: n.at, text: plain };
  let m: RegExpExecArray | null;
  // "X deposited $1,000,000" · "X deposited 100 points"
  if ((m = new RegExp(`^${WHO} deposited ${AMOUNT}`, 'i').exec(tokens))) return { ...base, action: 'deposit', who: personAt(people, m[1]), by: null, ...amountOf(m[2]!) };
  // "X was given $5,000,000 by Y"
  if ((m = new RegExp(`^${WHO} was given ${AMOUNT} by ${WHO}\\.?$`, 'i').exec(tokens))) return { ...base, action: 'give', who: personAt(people, m[1]), by: personAt(people, m[3]), ...amountOf(m[2]!) };
  // "Y gave $5,000,000 to X"
  if ((m = new RegExp(`^${WHO} gave ${AMOUNT} to ${WHO}\\.?$`, 'i').exec(tokens))) return { ...base, action: 'give', who: personAt(people, m[3]), by: personAt(people, m[1]), ...amountOf(m[2]!) };
  // "Y increased X's money balance by $1,000" · "Y decreased X's points balance by 50"
  if ((m = new RegExp(`^${WHO} (increased|decreased) ${WHO}'s? (money|points) balance by \\$?([\\d,]+)`, 'i').exec(tokens))) {
    const v = int(m[5]);
    return { ...base, action: 'adjust', who: personAt(people, m[3]), by: personAt(people, m[1]), amount: m[2]!.toLowerCase() === 'decreased' ? -v : v, kind: m[4]!.toLowerCase() === 'points' ? 'points' : 'cash' };
  }
  return { ...base, action: 'other', who: people[0] ?? null, by: people[1] ?? null, amount: 0, kind: 'cash' };
}

/** What each item went through, most used first. */
export function armoryByItem(lines: readonly ArmoryLine[]): Array<{ item: string; counts: Partial<Record<ArmoryAction, number>>; total: number }> {
  const out = new Map<string, { item: string; counts: Partial<Record<ArmoryAction, number>>; total: number }>();
  for (const l of lines) {
    if (!l.item) continue;
    const r = out.get(l.item) ?? { item: l.item, counts: {}, total: 0 };
    r.counts[l.action] = (r.counts[l.action] ?? 0) + l.count;
    r.total += l.count;
    out.set(l.item, r);
  }
  return [...out.values()].sort((a, b) => (b.counts.used ?? 0) - (a.counts.used ?? 0) || b.total - a.total || a.item.localeCompare(b.item));
}

/** What each member did in the armory (by who did it). */
export function armoryByMember(lines: readonly ArmoryLine[]): Array<{ who: OpsPerson; counts: Partial<Record<ArmoryAction, number>>; items: Record<string, number>; last: number }> {
  const out = new Map<string, { who: OpsPerson; counts: Partial<Record<ArmoryAction, number>>; items: Record<string, number>; last: number }>();
  for (const l of lines) {
    if (!l.who) continue;
    const k = l.who.id ? `#${l.who.id}` : l.who.name;
    const r = out.get(k) ?? { who: l.who, counts: {}, items: {}, last: 0 };
    r.counts[l.action] = (r.counts[l.action] ?? 0) + Math.max(1, l.count);
    if (l.item) r.items[l.item] = (r.items[l.item] ?? 0) + l.count;
    r.last = Math.max(r.last, l.at);
    out.set(k, r);
  }
  return [...out.values()].sort((a, b) => (b.counts.used ?? 0) - (a.counts.used ?? 0) || b.last - a.last);
}

/** Each member's money (or points) in and out: deposited, given out to them, balance changes, and the net. */
export function fundsByMember(lines: readonly FundsLine[], kind: 'cash' | 'points' = 'cash'): Array<{ who: OpsPerson; deposited: number; given: number; adjusted: number; net: number; last: number }> {
  const out = new Map<string, { who: OpsPerson; deposited: number; given: number; adjusted: number; net: number; last: number }>();
  for (const l of lines) {
    if (!l.who || l.kind !== kind || l.action === 'other') continue;
    const k = l.who.id ? `#${l.who.id}` : l.who.name;
    const r = out.get(k) ?? { who: l.who, deposited: 0, given: 0, adjusted: 0, net: 0, last: 0 };
    if (l.action === 'deposit') r.deposited += l.amount;
    else if (l.action === 'give') r.given += l.amount;
    else r.adjusted += l.amount;
    r.net = r.deposited - r.given + r.adjusted;
    r.last = Math.max(r.last, l.at);
    out.set(k, r);
  }
  return [...out.values()].sort((a, b) => b.deposited + b.given - (a.deposited + a.given));
}

export interface ChainSummary {
  id: number;
  /** Hits in the chain. */
  chain: number;
  respect: number;
  /** Seconds; end 0 while it goes on. */
  start: number;
  end: number;
}

export function readChains(body: unknown): ChainSummary[] {
  const out: ChainSummary[] = [];
  for (const raw of list(obj(body)?.chains)) {
    const c = obj(raw);
    const id = int(c?.id);
    if (!c || id <= 0) continue;
    out.push({ id, chain: int(c.chain), respect: numOr(c.respect, 0)!, start: int(c.start), end: int(c.end) });
  }
  return out.sort((a, b) => b.start - a.start);
}

export interface ChainAttacker {
  id: number;
  respect: number;
  average: number;
  best: number;
  hits: number;
  leave: number;
  mug: number;
  hospitalize: number;
  assists: number;
  retaliations: number;
  overseas: number;
  draws: number;
  escapes: number;
  losses: number;
  war: number;
  bonuses: number;
}

export interface ChainReport {
  id: number;
  factionId: number;
  start: number;
  end: number;
  details: { chain: number; respect: number; members: number; targets: number; war: number; best: number; leave: number; mug: number; hospitalize: number; assists: number; retaliations: number; overseas: number; draws: number; escapes: number; losses: number };
  /** Bonus hits (10, 25, 50 … 100k): who hit whom, at which hit, for how much respect. */
  bonuses: Array<{ attacker: number; defender: number; chain: number; respect: number }>;
  attackers: ChainAttacker[];
  /** Members who didn't hit in it. */
  nonAttackers: number[];
}

export function readChainReport(body: unknown): ChainReport | null {
  const r = obj(obj(body)?.chainreport) ?? obj(body);
  const id = int(r?.id);
  if (!r || id <= 0) return null;
  const d = obj(r.details) ?? {};
  const details = { chain: 0, respect: 0, members: 0, targets: 0, war: 0, best: 0, leave: 0, mug: 0, hospitalize: 0, assists: 0, retaliations: 0, overseas: 0, draws: 0, escapes: 0, losses: 0 };
  for (const k of Object.keys(details) as Array<keyof typeof details>) details[k] = numOr(d[k], 0)!;
  const bonuses = list(r.bonuses)
    .map(obj)
    .filter((b): b is Record<string, unknown> => !!b)
    .map((b) => ({ attacker: int(b.attacker_id), defender: int(b.defender_id), chain: int(b.chain), respect: numOr(b.respect, 0)! }))
    .filter((b) => b.chain > 0)
    .sort((a, b) => a.chain - b.chain);
  const attackers: ChainAttacker[] = [];
  for (const raw of list(r.attackers)) {
    const a = obj(raw);
    const aid = int(a?.id);
    if (!a || aid <= 0) continue;
    const rs = obj(a.respect) ?? {};
    const at = obj(a.attacks) ?? {};
    const n = (k: string) => int(at[k]);
    attackers.push({ id: aid, respect: numOr(rs.total, 0)!, average: numOr(rs.average, 0)!, best: numOr(rs.best, 0)!, hits: n('total'), leave: n('leave'), mug: n('mug'), hospitalize: n('hospitalize'), assists: n('assists'), retaliations: n('retaliations'), overseas: n('overseas'), draws: n('draws'), escapes: n('escapes'), losses: n('losses'), war: n('war'), bonuses: n('bonuses') });
  }
  attackers.sort((a, b) => b.respect - a.respect || b.hits - a.hits);
  const nonAttackers = list(r.non_attackers).map(int).filter((x) => x > 0);
  return { id, factionId: int(r.faction_id), start: int(r.start), end: int(r.end), details, bonuses, attackers, nonAttackers };
}

export interface OpsWarSide {
  id: number;
  name: string;
  score: number;
  aggressor: boolean;
}

/** A raid (or a territory war): who against whom, when, the score. */
export interface OpsWar {
  kind: 'raid' | 'territory';
  id: number;
  start: number;
  /** 0 while it goes on. */
  end: number;
  /** Territory wars: the territory's code and the score needed. */
  territory: string | null;
  target: number | null;
  /** Territory wars: Torn's result ("success", "fail", "timeout", "in_progress"…), winner when said. */
  result: string | null;
  winner: number | null;
  factions: OpsWarSide[];
}

const sideOf = (raw: unknown, aggressor?: boolean): OpsWarSide | null => {
  const f = obj(raw);
  const id = int(f?.id ?? f?.faction_id);
  if (!f || id <= 0) return null;
  return { id, name: String(f.name ?? `Faction ${id}`).slice(0, 60), score: numOr(f.score, 0)!, aggressor: aggressor ?? f.is_aggressor === true };
};

export function readRaids(body: unknown): OpsWar[] {
  const out: OpsWar[] = [];
  for (const raw of list(obj(body)?.raids)) {
    const w = obj(raw);
    const id = int(w?.id ?? w?.war_id);
    if (!w || id <= 0) continue;
    const factions = obj(w.aggressor) || obj(w.defender) ? [sideOf(w.aggressor, true), sideOf(w.defender, false)] : list(w.factions).map((f) => sideOf(f));
    out.push({ kind: 'raid', id, start: int(w.start), end: int(w.end), territory: null, target: null, result: null, winner: null, factions: factions.filter((f): f is OpsWarSide => !!f) });
  }
  return out.sort((a, b) => b.start - a.start);
}

export function readTerritoryWars(body: unknown): OpsWar[] {
  const out: OpsWar[] = [];
  for (const raw of list(obj(body)?.territorywars)) {
    const w = obj(raw);
    const id = int(w?.id ?? w?.war_id ?? w?.territory_war_id);
    if (!w || id <= 0) continue;
    const factions = list(w.factions).map((f) => sideOf(f)).filter((f): f is OpsWarSide => !!f);
    const winner = int(w.winner) || null;
    out.push({ kind: 'territory', id, start: int(w.start), end: int(w.end), territory: typeof w.territory === 'string' ? w.territory.slice(0, 8) : null, target: numOr(w.target ?? w.required_score), result: typeof w.result === 'string' ? w.result.slice(0, 20) : null, winner, factions });
  }
  return out.sort((a, b) => b.start - a.start);
}

export interface TerritoryHeld {
  id: string;
  sector: number;
  size: number;
  density: number;
  slots: number;
  /** Respect a day. */
  respect: number;
  acquiredAt: number;
  racket: { name: string; level: number; reward: string } | null;
}

export function readTerritories(body: unknown): TerritoryHeld[] {
  const out: TerritoryHeld[] = [];
  for (const raw of list(obj(body)?.territory)) {
    const t = obj(raw);
    const id = typeof t?.id === 'string' ? t.id.slice(0, 8) : '';
    if (!t || !id) continue;
    const r = obj(t.racket);
    const reward = obj(r?.reward);
    out.push({
      id,
      sector: int(t.sector),
      size: int(t.size),
      density: int(t.density),
      slots: int(t.slots),
      respect: numOr(t.respect, 0)!,
      acquiredAt: int(t.acquired_at),
      racket: r ? { name: String(r.name ?? '').slice(0, 60), level: int(r.level), reward: reward ? `${int(reward.quantity) || ''} ${String(reward.type ?? '')}`.trim().slice(0, 60) : String(r.reward ?? '').slice(0, 60) } : null,
    });
  }
  return out.sort((a, b) => b.respect - a.respect || a.id.localeCompare(b.id));
}

export interface FactionBalances {
  faction: { money: number; points: number } | null;
  members: Array<{ id: number; name: string; money: number; points: number }>;
}

export function readFactionBalances(body: unknown): FactionBalances {
  const b = obj(obj(body)?.balance);
  const f = obj(b?.faction);
  const members: FactionBalances['members'] = [];
  for (const raw of list(b?.members)) {
    const m = obj(raw);
    const id = int(m?.id);
    if (!m || id <= 0) continue;
    members.push({ id, name: String(m.username ?? m.name ?? '').slice(0, 40), money: int(m.money), points: int(m.points) });
  }
  return { faction: f ? { money: int(f.money), points: int(f.points) } : null, members: members.sort((a, b) => b.money - a.money) };
}

/**
 * A banker's link to Torn's "Give to user" form for one money move. Torn opens its own form; the Omni-Torn extension (or
 * PDA script) fills in who and how much (readGiveLink) and the banker presses Torn's button. Nothing is sent by itself.
 */
export function bankGiveLink(playerId: number, amount: number, kind: 'cash' | 'points' = 'cash'): string {
  const id = Math.max(0, Math.round(playerId));
  const n = Math.max(0, Math.round(amount));
  return `https://www.torn.com/factions.php?step=your&otGive=${id}&otAmount=${n}${kind === 'points' ? '&otKind=points' : ''}#/tab=controls&option=give-to-user`;
}

/** The give link's parts from a Torn address (its query string), or null when it isn't one. */
export function readGiveLink(search: string): { id: number; amount: number; kind: 'cash' | 'points' } | null {
  let p: URLSearchParams;
  try {
    p = new URLSearchParams(search);
  } catch {
    return null;
  }
  const id = Number(p.get('otGive'));
  const amount = Number(p.get('otAmount'));
  if (!Number.isSafeInteger(id) || id <= 0 || !Number.isSafeInteger(amount) || amount <= 0) return null;
  return { id, amount, kind: p.get('otKind') === 'points' ? 'points' : 'cash' };
}

/** What the server answers for one part. */
export interface FactionOpsAnswer {
  part: FactionOpsPart;
  /** When it was last read from Torn (ms), or null. */
  readAt: number | null;
  /** History is kept on the server (a key whose owner agreed to FACTION_HISTORY_TERMS); else only an hour, in memory. */
  kept: boolean;
  /** Days kept; 0 = no end date (terms KEEP_ALL_TERMS, "No age limits"). */
  keepDays: number;
  /** Plain words when it couldn't be read (no key with the faction's API access, Torn's refusal…). */
  problem: string | null;
  armory?: ArmoryLine[];
  funds?: FundsLine[];
  chains?: ChainSummary[];
  raids?: OpsWar[];
  territory?: TerritoryHeld[];
  /** Funds: only your own lines (bankers see every member's). */
  ownOnly?: boolean;
}

/** A vault request made in Discord (/withdraw), as the website shows it. */
export interface BankRequestView {
  id: number;
  tornId: number;
  tornName: string | null;
  amount: number;
  kind: 'cash' | 'points';
  status: 'pending' | 'sent' | 'cancelled' | 'expired';
  /** Seconds. */
  createdAt: number;
  expiresAt: number | null;
  doneAt: number | null;
  newsCheck: string | null;
}

export interface FactionBankAnswer {
  banker: boolean;
  /** Bankers: every member; others: their own (or none). */
  balances: FactionBalances | null;
  balancesAt: number | null;
  requests: BankRequestView[];
  /** The bot's banking isn't reachable (requests unknown). */
  requestsProblem: string | null;
  problem: string | null;
}
